// src/components/Experience.jsx
import React from "react";
import { motion } from "framer-motion";

import {
  FaReact,
  FaJava,
  FaDatabase,
  FaAws,
  FaGithub,
  FaHtml5,
  FaCss3Alt,
  FaJs,
  FaDocker,
  FaGitAlt,
  FaRobot,
  FaPuzzlePiece,
  FaNetworkWired,
  FaShieldAlt,
  FaImage,
  FaMicrophone,
  FaServer,
} from "react-icons/fa";

import {
  SiDotnet,
  SiMongodb,
  SiSpringboot,
  SiExpress,
  SiNextdotjs,
  SiFlask,
  SiTypescript,
  SiTailwindcss,
  SiPostgresql,
  SiMysql,
  SiPrisma,
  SiGraphql,
  SiCypress,
  SiJest,
  SiC,
  SiHibernate,
  SiPytest,
  SiKubernetes,
  SiAngular,
  SiGo,
  SiStreamlit,
  SiLangchain,
  SiOpenai,
  SiAnthropic,
  SiHuggingface,
  SiOllama,
  SiPython,
  SiFastapi,
  SiRedis,
} from "react-icons/si";

/* ===========================
   SKILL DATA
=========================== */

const skillSections = [
  {
    title: "Backend",
    icon: <FaServer />,
    summary: "Reliable service layers with clean architecture and pragmatic performance decisions.",
    skills: [
      { name: "Java", icon: <FaJava className="text-orange-500" /> },
      { name: "Spring Boot", icon: <SiSpringboot className="text-green-500" /> },
      { name: "Node.js", icon: <FaJs className="text-yellow-500" /> },
      { name: "Express", icon: <SiExpress className="text-slate-500" /> },
      { name: "Flask", icon: <SiFlask className="text-slate-700 dark:text-slate-300" /> },
      { name: "FastAPI", icon: <SiFastapi className="text-teal-500" /> },
      { name: "ASP.NET", icon: <SiDotnet className="text-blue-500" /> },
      { name: "Go", icon: <SiGo className="text-cyan-500" /> },
    ],
  },
  {
    title: "Frontend",
    icon: <FaReact />,
    summary: "Polished interfaces with reusable components and responsive layouts.",
    skills: [
      { name: "React", icon: <FaReact className="text-cyan-500" /> },
      { name: "Next.js", icon: <SiNextdotjs className="text-black dark:text-white" /> },
      { name: "Tailwind", icon: <SiTailwindcss className="text-teal-500" /> },
      { name: "TypeScript", icon: <SiTypescript className="text-blue-600" /> },
      { name: "JavaScript", icon: <FaJs className="text-yellow-500" /> },
      { name: "HTML", icon: <FaHtml5 className="text-orange-500" /> },
      { name: "CSS", icon: <FaCss3Alt className="text-blue-500" /> },
      { name: "Angular", icon: <SiAngular className="text-red-500" /> },
    ],
  },
  {
    title: "AI / LLM Engineering",
    icon: <FaRobot />,
    summary: "Applied generative AI — from prompting and retrieval to agents and production deployment.",
    skills: [
      { name: "Prompt Engineering", icon: <FaRobot className="text-pink-500" /> },
      { name: "RAG Pipelines", icon: <FaDatabase className="text-pink-500" /> },
      { name: "Vector Search", icon: <FaDatabase className="text-indigo-500" /> },
      { name: "AI Agents", icon: <FaRobot className="text-violet-500" /> },
      { name: "Model Context Protocol", icon: <FaPuzzlePiece className="text-emerald-500" /> },
      { name: "Multi-Agent Systems", icon: <FaNetworkWired className="text-sky-500" /> },
      { name: "Prompt-Injection Defense", icon: <FaShieldAlt className="text-rose-500" /> },
      { name: "Multimodal AI", icon: <FaImage className="text-amber-500" /> },
      { name: "Speech & Audio AI", icon: <FaMicrophone className="text-amber-500" /> },
      { name: "LangChain", icon: <SiLangchain className="text-emerald-500" /> },
      { name: "OpenAI API", icon: <SiOpenai className="text-slate-700 dark:text-slate-200" /> },
      { name: "Anthropic Claude", icon: <SiAnthropic className="text-orange-500" /> },
      { name: "Hugging Face", icon: <SiHuggingface className="text-yellow-500" /> },
      { name: "Ollama", icon: <SiOllama className="text-slate-700 dark:text-slate-200" /> },
      { name: "Python", icon: <SiPython className="text-yellow-500" /> },
    ],
  },
  {
    title: "System Design",
    icon: <FaNetworkWired />,
    summary: "LLD to HLD — architecture, data modeling, and trade-offs under scale.",
    skills: [
      { name: "SQL", icon: <FaDatabase className="text-green-600" /> },
      { name: "PostgreSQL", icon: <SiPostgresql className="text-blue-400" /> },
      { name: "MySQL", icon: <SiMysql className="text-sky-500" /> },
      { name: "MongoDB", icon: <SiMongodb className="text-green-500" /> },
      { name: "Redis", icon: <SiRedis className="text-red-500" /> },
    ],
  },
  {
    title: "DevOps & Cloud",
    icon: <FaAws />,
    summary: "Deployment discipline, cloud services, containers, and automation.",
    skills: [
      { name: "Docker", icon: <FaDocker className="text-blue-500" /> },
      { name: "AWS", icon: <FaAws className="text-orange-500" /> },
      { name: "Kubernetes", icon: <SiKubernetes className="text-blue-500" /> },
      { name: "GitHub Actions", icon: <FaGithub className="text-slate-600 dark:text-slate-300" /> },
    ],
  },
  {
    title: "Tools & Testing",
    icon: <FaGitAlt />,
    summary: "Practical tooling to keep code maintainable, testable, and observable.",
    skills: [
      { name: "GraphQL", icon: <SiGraphql className="text-pink-500" /> },
      { name: "Prisma", icon: <SiPrisma className="text-indigo-500" /> },
      { name: "Pytest", icon: <SiPytest className="text-yellow-500" /> },
      { name: "Jest", icon: <SiJest className="text-rose-500" /> },
      { name: "Cypress", icon: <SiCypress className="text-emerald-500" /> },
      { name: "Hibernate", icon: <SiHibernate className="text-amber-600" /> },
      { name: "Git", icon: <FaGitAlt className="text-orange-500" /> },
      { name: "C / C++", icon: <SiC className="text-blue-500" /> },
      { name: "Streamlit", icon: <SiStreamlit className="text-red-500" /> },
    ],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

const Experience = () => {
  return (
    <section name="Skills" className="aurora-section px-6 py-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 max-w-2xl">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white md:text-4xl">
            Technical <span className="accent-text">Skills</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-zinc-400">
            Technologies across backend systems, cloud platforms, and
            applied AI engineering — including the generative AI stack
            I've been building with: prompting, retrieval, agents, and
            production LLM deployment.
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          {skillSections.map((section, index) => (
            <motion.article
              key={section.title}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (index % 2) * 0.08 }}
              className="panel p-6"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-lg text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
                  {section.icon}
                </span>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  {section.title}
                </h3>
              </div>

              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-zinc-400">
                {section.summary}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {section.skills.map((skill) => (
                  <span
                    key={skill.name}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200"
                  >
                    <span className="text-sm">{skill.icon}</span>
                    {skill.name}
                  </span>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
